/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TooltipsTab.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Tooltips tab component for Appearance settings.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { MessageSquare, Moon, Sun } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { getInputBackgroundColor } from '../../../store/theme';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import type { AppearanceForm } from '../types';
import type { UseFormRegister, UseFormSetValue } from 'react-hook-form';

interface TooltipsTabProps {
  watchedValues: AppearanceForm;
  register: UseFormRegister<AppearanceForm>;
  setValue: UseFormSetValue<AppearanceForm>;
}

export function TooltipsTab({ watchedValues, register, setValue }: TooltipsTabProps) {
  const { t } = useI18n();

  const isDarkMode = watchedValues.mode === 'dark';
  const themeKey = isDarkMode ? 'darkTheme' : 'lightTheme';
  const ThemeIcon = isDarkMode ? Moon : Sun;
  const themeLabel = isDarkMode
    ? t('appearance.tooltips.darkTheme.title')
    : t('appearance.tooltips.lightTheme.title');

  const tooltipBgColor = isDarkMode
    ? (watchedValues.tooltips?.darkTheme?.backgroundColor || getInputBackgroundColor('dark', undefined, watchedValues.darkModeVariant || 'very-dark-gray'))
    : (watchedValues.tooltips?.lightTheme?.backgroundColor || getInputBackgroundColor('light', watchedValues.lightModeVariant || 'white'));

  const tooltipTextColor = isDarkMode
    ? (watchedValues.tooltips?.darkTheme?.textColor || '#F1F5F9')
    : (watchedValues.tooltips?.lightTheme?.textColor || '#1E293B');

  const tooltipBorderRadius = isDarkMode
    ? (watchedValues.tooltips?.darkTheme?.borderRadius || 0.5)
    : (watchedValues.tooltips?.lightTheme?.borderRadius || 0.5);

  const placeholderBg = isDarkMode
    ? getInputBackgroundColor('dark', undefined, watchedValues.darkModeVariant || 'very-dark-gray')
    : getInputBackgroundColor('light', watchedValues.lightModeVariant || 'white');

  return (
    <div className="space-y-3">
      <FormBox title={t('appearance.tooltips.title')} icon={<MessageSquare />}>
        {/* Active Theme Tooltips Configuration */}
        <div>
          <h3 className="text-md font-medium text-foreground mb-3 flex items-center gap-2">
            <ThemeIcon className="h-4 w-4" />
            {themeLabel}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <SimpleTooltip content="Background color of the tooltip bubble." position="top">
                <label className="block text-sm font-medium text-foreground mb-2 cursor-default w-fit">
                  {t('appearance.tooltips.backgroundColor')}
                </label>
              </SimpleTooltip>
              <div className="flex gap-2">
                <input
                  {...register(`tooltips.${themeKey}.backgroundColor`)}
                  type="color"
                  className="w-12 h-10 rounded border border-input cursor-pointer p-0 overflow-hidden bg-transparent"
                  onChange={(e) => {
                    setValue(`tooltips.${themeKey}.backgroundColor`, e.target.value);
                  }}
                />
                <input
                  {...register(`tooltips.${themeKey}.backgroundColor`)}
                  type="text"
                  placeholder={placeholderBg}
                  className="flex-1 px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
                      setValue(`tooltips.${themeKey}.backgroundColor`, value);
                    }
                  }}
                />
              </div>
            </div>
            <div>
              <SimpleTooltip content="Text color inside the tooltip." position="top">
                <label className="block text-sm font-medium text-foreground mb-2 cursor-default w-fit">
                  {t('appearance.tooltips.textColor')}
                </label>
              </SimpleTooltip>
              <div className="flex gap-2">
                <input
                  {...register(`tooltips.${themeKey}.textColor`)}
                  type="color"
                  className="w-12 h-10 rounded border border-input cursor-pointer p-0 overflow-hidden bg-transparent"
                  onChange={(e) => {
                    setValue(`tooltips.${themeKey}.textColor`, e.target.value);
                  }}
                />
                <input
                  {...register(`tooltips.${themeKey}.textColor`)}
                  type="text"
                  placeholder={isDarkMode ? "#F1F5F9" : "#1E293B"}
                  className="flex-1 px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
                      setValue(`tooltips.${themeKey}.textColor`, value);
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div>
              <SimpleTooltip content="Controls how rounded the tooltip corners are." position="top">
                <label className="block text-sm font-medium text-foreground mb-2 cursor-default w-fit">
                  {t('appearance.tooltips.borderRadius')}: {tooltipBorderRadius}rem
                </label>
              </SimpleTooltip>
              <input
                {...register(`tooltips.${themeKey}.borderRadius`, { valueAsNumber: true })}
                type="range"
                min="0"
                max="1"
                step="0.1"
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Tooltip Preview */}
        <div className="mt-6">
          <h3 className="text-sm font-medium text-foreground mb-4">{t('appearance.tooltips.preview.title')}</h3>
          <div className="flex justify-center">
            <div className="relative">
              {/* Tooltip Preview with Arrow */}
              <div className="relative flex justify-center">
                <div
                  className="px-4 py-3 text-base shadow-lg relative"
                  style={{
                    backgroundColor: tooltipBgColor,
                    color: tooltipTextColor,
                    opacity: 1,
                    borderRadius: `${tooltipBorderRadius}rem`
                  }}
                >
                  <div className="text-sm opacity-90">{t('appearance.tooltips.preview.description')}</div>

                  {/* SVG arrow — same technique as TooltipPrimitive.Arrow */}
                  <svg
                    width="12"
                    height="6"
                    viewBox="0 0 12 6"
                    className="absolute top-full left-6"
                    style={{ fill: tooltipBgColor, overflow: 'visible' }}
                  >
                    <polygon points="0,0 6,6 12,0" />
                  </svg>
                </div>
              </div>

              {/* Current theme indicator */}
              <div className="mt-4 text-center">
                <span className="text-xs text-muted-foreground">
                  {t('appearance.tooltips.preview.currentTheme')}:
                  <span className="font-medium ml-1">
                    {watchedValues.mode === 'dark'
                      ? t('appearance.themeMode.dark.label')
                      : t('appearance.themeMode.light.label')}
                    {watchedValues.mode !== 'dark' && ` + ${t('appearance.lightModeVariant.' + (watchedValues.lightModeVariant || 'white') + '.label')}`}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </FormBox>
    </div>
  );
}

